/**
 * Utility functions for URL slug generation
 */

/**
 * Create a URL-friendly slug from a title
 * @param {string} title - The title to convert to a slug
 * @returns {string} URL-friendly slug
 */
export function createSlugFromTitle(title) {
  if (!title || typeof title !== 'string') {
    return '';
  }
  
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric except spaces and hyphens
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Replace multiple hyphens with single hyphen
}

/**
 * Get the article URL from metadata item
 * @param {Object} article - Article metadata object
 * @returns {string} Article URL path
 */
export function getArticleUrl(article) {
  if (!article || !article.title) {
    return '';
  }
  
  // For external links (like YouTube videos), return the content_path directly
  if (article.content_path && article.content_path.startsWith('http')) {
    return article.content_path;
  }
  
  // For blog posts, create URL from title
  if (article.type === 'blogPosts') {
    const slug = createSlugFromTitle(article.title);
    return `/article/${slug}`;
  }
  
  // Fallback to content_path based slug (for backward compatibility)
  if (article.content_path) {
    const slug = article.content_path.replace('.md', '');
    return `/article/${slug}`;
  }
  
  return '';
}

/**
 * Find article by slug in metadata array
 * @param {Array} metadata - Array of article metadata
 * @param {string} slug - URL slug to search for
 * @returns {Object|null} Found article or null
 */
export function findArticleBySlug(metadata, slug) {
  if (!metadata || !Array.isArray(metadata) || !slug) {
    return null;
  }
  
  return metadata.find(item => {
    if (item.type !== 'blogPosts' || !item.title) {
      return false;
    }
    
    const titleSlug = createSlugFromTitle(item.title);
    return titleSlug === slug;
  });
}

export default {
  createSlugFromTitle,
  getArticleUrl,
  findArticleBySlug
};
